import React, { useState, useEffect, useRef } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import "../styles/VicharanMap.css";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getYoutubeId(url) {
  if (!url || url === "NULL") return null;
  try {
    // youtube.com/watch?v=ID
    const match = url.match(/[?&]v=([^&]+)/);
    if (match) return match[1];
    // youtu.be/ID
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) return shortMatch[1];
    // youtube.com/shorts/ID
    const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&/]+)/);
    if (shortsMatch) return shortsMatch[1];
    // youtube.com/embed/ID
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&/]+)/);
    if (embedMatch) return embedMatch[1];
    // youtube.com/live/ID
    const liveMatch = url.match(/youtube\.com\/live\/([^?&/]+)/);
    if (liveMatch) return liveMatch[1];
  } catch (e) {}
  return null;
}

function getPhotoThumbnailUrl(photourl, seq = "01") {
  if (!photourl || photourl === "NULL") return null;
  try {
    const url = new URL(photourl);
    const year = url.searchParams.get("year");
    const month = url.searchParams.get("month");
    const folder = url.searchParams.get("folder");
    if (year && month && folder) {
      const datepart = folder.match(/^\d{4}-\d{2}-\d{2}/)?.[0] || folder;
      return `https://smruti.ihariprabodham.in/${year}/${month}/${folder}/thumbnail/${datepart}-${seq}.jpg`;
    }
  } catch (e) {}
  return null;
}

function VicharanMap() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null); // array of entries at clicked pin
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [infoModal, setInfoModal] = useState(null); // { type: 'info'|'paramrut', content }

  // Load JSON data
  useEffect(() => {
    fetch("/dailydetail/dailydetail-12.json")
      .then((res) => res.json())
      .then((data) => {
        setAllData(data);
        const years = [...new Set(data.map((d) => d.infoyear))].sort();
        setAvailableYears(years);
        if (years.length > 0) setSelectedYear(years[0]);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Initialize map once data is loaded
  useEffect(() => {
    if (loading || !mapRef.current || mapInstanceRef.current) return;
    let cancelled = false;
    (async () => {
      setOptions({
        key: "AIzaSyCaYk1l7L9STHEzAgamAeVcUsbrWTtdd9M",
        version: "weekly",
      });
      const { Map } = await importLibrary("maps");
      if (cancelled) return;
      mapInstanceRef.current = new Map(mapRef.current, {
        mapId: "15431d2b469f209e",
        center: { lat: 22.5, lng: 72.5 },
        zoom: 6,
        gestureHandling: "greedy",
      });
      setMapReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [loading]);

  // Place markers whenever year or map readiness changes
  useEffect(() => {
    if (!mapReady || !allData.length || !selectedYear) return;
    let cancelled = false;
    (async () => {
      const { AdvancedMarkerElement } = await importLibrary("marker");
      if (cancelled) return;

      // Clear old markers
      markersRef.current.forEach((m) => {
        m.map = null;
      });
      markersRef.current = [];

      // Filter by year and valid coords
      const filtered = allData.filter(
        (d) => d.infoyear === selectedYear && !!d.latitude && !!d["longitude"],
      );

      // Group by exact lat/lng so overlapping entries share one pin
      const groups = {};
      filtered.forEach((entry) => {
        const key = `${entry.latitude}_${entry["longitude"]}`;
        if (!groups[key]) {
          groups[key] = {
            lat: entry.latitude,
            lng: entry["longitude"],
            entries: [],
          };
        }
        groups[key].entries.push(entry);
      });

      Object.values(groups).forEach((group) => {
        const pinEl = document.createElement("div");
        pinEl.className = "vm-map-pin";

        const label = document.createElement("span");
        label.className = "vm-map-pin-label";
        label.textContent = group.entries[0].city.trim();
        pinEl.appendChild(label);

        if (group.entries.length > 1) {
          const badge = document.createElement("span");
          badge.className = "vm-map-pin-count";
          badge.textContent = group.entries.length;
          pinEl.appendChild(badge);
        }

        const marker = new AdvancedMarkerElement({
          position: { lat: group.lat, lng: group.lng },
          map: mapInstanceRef.current,
          title: group.entries[0].place,
          content: pinEl,
        });

        marker.addListener("click", () => {
          setSelectedGroup(group.entries);
          setSelectedIndex(0);
        });

        markersRef.current.push(marker);
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [mapReady, selectedYear, allData]);

  const entry = selectedGroup ? selectedGroup[selectedIndex] : null;
  const closePopup = () => {
    setSelectedGroup(null);
    setSelectedIndex(0);
  };

  return (
    <div className="vm-page themed-map-page">
      <div className="vm-page-header">
        <h1 className="vm-main-title">Guruhari Vicharan Map</h1>
        <p className="vm-sub-title">ગુરુહરિ વિચરણ નકશો</p>
      </div>

      {loading ? (
        <div className="vm-loading">Loading...</div>
      ) : (
        <>
          <div className="vm-year-tabs" role="group" aria-label="Years">
            {availableYears.map((y) => (
              <button
                key={y}
                className={`vm-year-tab ${selectedYear === y ? "vm-year-tab--active" : ""}`}
                aria-pressed={selectedYear === y}
                onClick={() => {
                  setSelectedYear(y);
                  setSelectedGroup(null);
                }}
              >
                {y}
              </button>
            ))}
          </div>

          <div className="vm-map-wrap">
            <div ref={mapRef} className="vm-map" />
          </div>
        </>
      )}

      {/* Day-cell popup on pin click */}
      {entry && (
        <div className="vm-popup-overlay" onClick={closePopup}>
          <div className="vm-popup" onClick={(e) => e.stopPropagation()}>
            <button className="vm-popup-close" aria-label="Close visit" onClick={closePopup}>
              ✕
            </button>

            {selectedGroup.length > 1 && (
              <div className="vm-popup-nav">
                <button
                  className="vm-popup-nav-btn"
                  aria-label="Previous visit"
                  disabled={selectedIndex === 0}
                  onClick={() => setSelectedIndex((i) => i - 1)}
                >
                  &#8249;
                </button>
                <span>
                  {selectedIndex + 1} / {selectedGroup.length}
                </span>
                <button
                  className="vm-popup-nav-btn"
                  aria-label="Next visit"
                  disabled={selectedIndex === selectedGroup.length - 1}
                  onClick={() => setSelectedIndex((i) => i + 1)}
                >
                  &#8250;
                </button>
              </div>
            )}

            <div className="vm-popup-date">
              {entry.infoday} {monthNames[entry.infomonth - 1]} {entry.infoyear}
            </div>
            <div className="vm-popup-city">{entry.place}</div>

            {entry.shortdescguj && (
              <div
                className="vm-popup-desc"
                onClick={() =>
                  setInfoModal({ type: "info", content: entry.shortdescguj })
                }
              >
                {entry.shortdescguj}
              </div>
            )}

            <div className="vm-popup-thumbnails">
              {getYoutubeId(entry.videourl) && (
                <a
                  href={entry.videourl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vm-thumb-link"
                  title="Watch on YouTube"
                >
                  <img
                    src={`https://img.youtube.com/vi/${getYoutubeId(entry.videourl)}/mqdefault.jpg`}
                    alt="YouTube"
                    className="vm-thumb vm-thumb--yt"
                  />
                  <span className="vm-thumb-badge">▶</span>
                </a>
              )}
              {entry.photourl && entry.photourl !== "NULL" && (
                <a
                  href={entry.photourl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vm-thumb-link"
                  title="View Photos"
                >
                  <img
                    src={
                      getPhotoThumbnailUrl(entry.photourl) ||
                      "/dailydetail/thumbnail.jpg"
                    }
                    alt="Photos"
                    className="vm-thumb vm-thumb--photo"
                    onError={(e) => {
                      const alt = getPhotoThumbnailUrl(entry.photourl, "001");
                      if (alt && e.target.src !== alt) {
                        e.target.src = alt;
                      } else {
                        e.target.src = "/dailydetail/thumbnail.jpg";
                      }
                    }}
                  />
                </a>
              )}
              {getYoutubeId(entry.highlighturl) && (
                <a
                  href={entry.highlighturl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vm-thumb-link"
                  title="Watch Highlight"
                >
                  <img
                    src={`https://img.youtube.com/vi/${getYoutubeId(entry.highlighturl)}/mqdefault.jpg`}
                    alt="Highlight"
                    className="vm-thumb vm-thumb--yt"
                  />
                  <span className="vm-thumb-badge vm-thumb-badge--hl">H</span>
                </a>
              )}
              {getYoutubeId(entry.clipurl1) && (
                <a
                  href={entry.clipurl1}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vm-thumb-link"
                  title="Watch Clip 1"
                >
                  <img
                    src={`https://img.youtube.com/vi/${getYoutubeId(entry.clipurl1)}/mqdefault.jpg`}
                    alt="Clip 1"
                    className="vm-thumb vm-thumb--yt"
                  />
                  <span className="vm-thumb-badge vm-thumb-badge--cl">C1</span>
                </a>
              )}
              {getYoutubeId(entry.clipurl2) && (
                <a
                  href={entry.clipurl2}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vm-thumb-link"
                  title="Watch Clip 2"
                >
                  <img
                    src={`https://img.youtube.com/vi/${getYoutubeId(entry.clipurl2)}/mqdefault.jpg`}
                    alt="Clip 2"
                    className="vm-thumb vm-thumb--yt"
                  />
                  <span className="vm-thumb-badge vm-thumb-badge--cl">C2</span>
                </a>
              )}
            </div>

            <div className="vm-popup-icons">
              {entry.flagparamrutapp === 1 && (
                <button
                  className="vm-icon-btn"
                  title="Pravachan on Paramrut App"
                  onClick={() =>
                    setInfoModal({ type: "paramrut", content: null })
                  }
                >
                  <img
                    src="/image/paramrut.jpg"
                    alt="Paramrut"
                    className="vm-paramrut-icon"
                  />
                </button>
              )}
              {!!entry.latitude && !!entry["longitude"] && (
                <a
                  href={`https://www.google.com/maps?q=${entry.latitude},${entry["longitude"]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vm-icon-btn vm-icon-btn--map"
                  title="Open in Google Maps"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="vm-map-svg"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Info / Paramrut modal (nested above popup) */}
      {infoModal && (
        <div className="vm-modal-overlay" onClick={() => setInfoModal(null)}>
          <div className="vm-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="vm-modal-close"
              aria-label="Close details"
              onClick={() => setInfoModal(null)}
            >
              ✕
            </button>
            {infoModal.type === "paramrut" && (
              <>
                <img
                  src="/image/paramrut.jpg"
                  alt="Paramrut"
                  className="vm-modal-paramrut-img"
                />
                <p className="vm-modal-text">
                  Pravachan Is Available In Paramrut App
                </p>
              </>
            )}
            {infoModal.type === "info" && (
              <>
                <div className="vm-modal-icon">વિગત</div>
                <p className="vm-modal-text vm-modal-text--guj">
                  {infoModal.content}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VicharanMap;
